import { ITableChange } from '@vinnova/ui';
import { Worksite } from '../../../core/models/question/question-worksite.model';

export interface IProjektBudget {
  startDatum: string;
  slutDatum: string;
  projektparterInvalid: boolean;
  projektparter: IProjektpart[];
  budgetNiva: unknown;
  hasChanges?: boolean;
}

export interface IProjektpart {
  id: string;
  koordinerande: boolean;
  kostnader: Kostnadsslag[];
  bidragPerAr: SummaPerAr[];
  finansiarer: Finansiar[];
  kostnaderChanges: ITableChange[];
  finansieringChanges: ITableChange[];
  maxStodniva: {
    varde: number;
    vardeDecimal?: number;
    andringsmarkering?: Andringsmarkering[];
  };
  stodgrund: {
    varde: number;
    vardeDecimal?: number;
    andringsmarkering?: Andringsmarkering[];
  };
  sfsStodgrund: {
    varde: number;
    vardeDecimal?: number;
    andringsmarkering?: Andringsmarkering[];
  };
  beraknadStodniva: {
    varde: number;
    vardeDecimal?: number;
    andringsmarkering?: Andringsmarkering[];
  };
  isNew: boolean;
  konsultkostnadOverskridenForklaring: string;
  sokerBidragIckeEkonomiskVerksamhet?: string;
}

export interface EffektaBudgetAnswer {
  projektparter: IProjektpart[];
  budgetNiva?: string;
  gamlaSlutAret?: Date;
  gamlaStartAret?: Date;
  kostnaderTotalChanges?: ITableChange[];
  finansieringTotalChanges?: ITableChange[];
  beraknadStodnivaTotalt?: {
    varde: number;
    vardeDecimal?: number;
    andringsmarkering?: Andringsmarkering[];
  };
}

export interface Andringsmarkering {
  text: string;
  textEn: string;
  varde: string;
  datum: string;
  vardeDecimal?: number;
  ordning?: number;
}

export type BudgetTableChanges = ITableChange[][][];

export type Finansiar = {
  id: string;
  finansieringPerAr: SummaPerAr[];
};

export type Kostnadsslag = {
  kostnadsslag: number;
  kostnaderPerAr: SummaPerAr[];
};

export type SummaPerAr = {
  ar: number;
  summa: number;
  andringsmarkeringar: Andringsmarkering[];
};

export type Projektpart = {
  worksite: Worksite;
  financiers: Worksite[];
};

export type ProjektpartList = Array<Projektpart>;

export type Parter = {
  koordinator: Projektpart;
  projektparter: ProjektpartList;
};

export type EffektaTableData = {
  rows: number[];
  columns: number[];
  totalSum: number;
};

export type EffektaTableDataMap = Map<string, EffektaTableData>;

export enum Standardkostnadstyp {
  TOTALKOSTNAD = '1',
}

export enum Kostnadstyp {
  LONEKOSTNADER = '1',
  UTRUSTNINGSKOSTNADER = '2',
  KONSULTKOSTNADER = '3',
  OVRIGAKOSTNADER = '4',
  INDIREKTAKOSTNADER = '5',
}

export enum Finansieringstyp {
  VINNOVABIDRAG = '1',
  ANNANFINANSIERING = '2',
  EGENFINANSIERING = '3',
}

// Backend budget item med optional fält
export interface BudgetItem {
  andringsmarkeringar?: Andringsmarkering[];
  ar?: number;
  typ?: number;
  varde?: number;
}

// Projektpart från backend response med ändringsmarkeringar
export interface ProjektpartMedAndringar {
  id?: string;
  kostnader?: Kostnadsslag[];
  summaKostnader?: BudgetItem[];
  kostnaderAggregeratPerKostnadsslag?: BudgetItem[];
  kostnaderTotalt?: BudgetItem;
  bidragPerAr?: BudgetItem[];
  finansiarer?: Array<Finansiar & { finansieringTotalt?: BudgetItem }>;
  egenFinansiering?: BudgetItem[];
  egenFinansieringTotalt?: BudgetItem;
  totalFinansiering?: BudgetItem[];
  bidragVinnovaTotalt?: BudgetItem;
  totalFinansieringTotalt?: BudgetItem;
  kostnaderChanges?: ITableChange[];
  finansieringChanges?: ITableChange[];
  maxStodniva?: {
    varde: number;
    vardeDecimal?: number;
    andringsmarkering?: Andringsmarkering[];
  };
  stodgrund?: {
    varde: number;
    vardeDecimal?: number;
    andringsmarkering?: Andringsmarkering[];
  };
}

// Backend response för budget med ändringsmarkeringar
export interface BudgetMedAndringar {
  accessRight?: number;
  budgetNiva?: string;
  gamlaSlutAret?: number;
  koordinatorId?: string;
  projektTitel?: string;
  projektTitelEn?: string;
  projektparter?: ProjektpartMedAndringar[];
  senastBeslut?: Date;
  slutDatum?: Date;
  startDatum?: Date;
  begaranInskickadDatum?: Date;
  senastUppdaterad?: Date;
  senastUppdateradInfoString?: string;
  worksites: Worksite[];
  totalVinnovaFinansiering?: number;
  // Budget totals med ändringsmarkeringar
  kostnaderTotal?: Kostnadsslag[];
  summaKostnaderTotal?: BudgetItem[];
  kostnaderAggregeratPerKostnadsslagTotal?: BudgetItem[];
  kostnaderTotalt?: BudgetItem;
  bidragVinnovaTotalt?: BudgetItem[];
  andraFinansiarerTotal?: BudgetItem[];
  övrigaFinansiarerTotalt?: BudgetItem;
  egenFinansieringTotalt?: BudgetItem[];
  totalEgenFinansieringTotalt?: BudgetItem;
  finansieringTotalt?: BudgetItem[];
  totalBidragVinnovaTotalt?: BudgetItem;
  totalFinansieringTotalt?: BudgetItem;
}
